import React from "react";
import { LOGO_SRC, inkOn } from "./studioLook";

// The product's own overlays, drawn to its render-service templates. In
// studio.css every size is in canvas pixels (1920 × 1080) times --u, so the
// numbers match the product's and the frame scales as one picture.

// The person in the frame until the visitor's camera takes their place: the
// product's own stand-in, head and shoulders.
export const You = () => (
  <svg className="st-you" viewBox="0 0 800 800" aria-hidden="true">
    <circle cx="400" cy="222" r="162" />
    <path d="M71 800V740C71 553 219 404 400 404S729 553 729 740V800Z" />
  </svg>
);

// The "classic" lower third: the logo box, then the name strip over the title
// band in the brand colour.
export const LowerThird = ({ name, title, brand }) => (
  <span className="st-lt" style={{ "--band": brand, "--band-ink": inkOn(brand) }}>
    <span className="st-lt-logo"><img src={LOGO_SRC} alt="" loading="lazy" /></span>
    <span className="st-lt-text">
      <span className="st-lt-name">{name}</span>
      <span className="st-lt-title">{title}</span>
    </span>
  </span>
);

// The scrolling text along the bottom edge, here carrying the warnings.
export const Ticker = ({ text }) => (
  <span className="st-tk">
    <span className="st-tk-run">
      <span>{text}</span>
      <span>{text}</span>
    </span>
  </span>
);

// The "Title" text preset, top left.
export const TitleText = ({ children }) => <span className="st-ti">{children}</span>;

// Content filling the right half of a side-by-side scene.
export const Panel = ({ title, children }) => (
  <span className="st-slot st-slot-side">
    <span className="st-panel">
      <strong className="st-panel-t">{title}</strong>
      {children}
    </span>
  </span>
);

export const Bullets = ({ items }) => (
  <span className="st-bullets">
    {items.map((item) => <span key={item}>{item}</span>)}
  </span>
);

// The product's donut: a ring of shares with its legend below.
export const Donut = ({ shares }) => {
  let start = 0;
  return (
    <span className="st-donut">
      <svg viewBox="0 0 42 42" aria-hidden="true">
        {shares.map(({ label, value, color }) => {
          const ring = (
            <circle key={label} cx="21" cy="21" r="15.915" fill="none" stroke={color} strokeWidth="5.4"
              strokeDasharray={`${value - 0.6} ${100.6 - value}`} strokeDashoffset={25 - start} />
          );
          start += value;
          return ring;
        })}
      </svg>
      <span className="st-legend">
        {shares.map(({ label, value, color }) => (
          <span key={label}><i style={{ background: color }} />{label}<b>{value}%</b></span>
        ))}
      </span>
    </span>
  );
};

// The content box an immersive scene puts on the studio's screen.
export const Screen = ({ children }) => <span className="st-slot st-slot-screen">{children}</span>;

// The product's line chart, at the size of that box (880 × 495).
export const LineChart = ({ series, labels, values }) => {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const x = (i) => 70 + (i * 740) / (values.length - 1);
  const y = (v) => 400 - ((v - lo) / (hi - lo)) * 260;
  const line = values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  return (
    <svg className="st-chart" viewBox="0 0 880 495" aria-hidden="true">
      <rect x="40" y="38" width="22" height="10" rx="5" className="st-chart-key" />
      <text x="72" y="49" className="st-chart-name">{series}</text>
      {[140, 227, 314, 401].map((gy) => <line key={gy} x1="40" x2="840" y1={gy} y2={gy} className="st-chart-grid" />)}
      <polygon points={`${x(0)},401 ${line} ${x(values.length - 1)},401`} className="st-chart-area" />
      <polyline points={line} className="st-chart-line" />
      {values.map((v, i) => <circle key={labels[i]} cx={x(i)} cy={y(v)} r="7" className="st-chart-dot" />)}
      {labels.map((label, i) => <text key={label} x={x(i)} y="456" className="st-chart-x">{label}</text>)}
    </svg>
  );
};
